import { createPublicItemDetailLink, router } from "@/router";
import { CategoryId, type Category } from "@/services/collection/categories";
import { fetchCategoryById } from "@/services/collection/categories/categoriesApi";
import { updateCollectionItem } from "@/services/collection/item/collectionItemApi";
import { type ApiData_UpdateCollectionItem, type CollectionItemId, type CollectionItem, type CollectionItemWithDetail, type CollectionItemWithDetail_LegoSet, type CollectionItemWithCollectible } from "@/services/collection/item/collectionItemTypes";
import { Event, EventBus } from "@/services/eventBus";
import { factoryImage, type Image } from "@/services/images";
import { createEmptyPrice, type PriceNumericOrEmpty } from "@/services/prices";
import { type InjectionKey, type Ref } from "vue";
import { gettext } from "@/services/translation";
import { getDomain, getProtocol } from "@/utilities";
import type { CollectibleElastic } from "@/services/collectibles/elastic";

/** @deprecated Try to use useCollectionItemStatus composable instead.  */
export const isPublic = (item: CollectionItem): boolean => {
  return item.userCollectionList.length > 0;
};

export const makeCollectionItemPublic = (item: CollectionItem): void => {
  item.userCollectionList = [""];
};

export const setCollectionItemQuantity = (item: CollectionItem, newQuantity: number): void => {
  item.pieces = newQuantity;
};

export const setDescription = (item: CollectionItem, description: string): void => {
  item.publicNote = description;
};

export const setAcquireDate = (item: CollectionItem, newAcquireDate: string): void => {
  item.dateAcquired = newAcquireDate;
};

export const unassignMarketplaceItem = (item: CollectionItem): void => {
  item.marketPlaceItem = null;
};

export const getCategory = async (item: CollectionItem): Promise<Category> => fetchCategoryById(item.categoryId);

/** @deprecated Use useCollectionItemElastic.shareableUrl instead. */
export const createShareableUrl = (itemId: CollectionItemId, itemName: string, ownerNickname: string): string => {
  const relativeUrl = createPublicItemDetailLink(itemId, itemName, ownerNickname);
  return getProtocol() + "//" + getDomain() + router.resolve(relativeUrl).fullPath;
};

export const createImageObjectsArray = (item: CollectionItem): Image[] => item.images.map(({ id, imageUrl }) => factoryImage(id, imageUrl, item.name));

export const getCurrentPrice = (item: CollectionItem): PriceNumericOrEmpty => {
  if (item.currentPrice !== undefined) {
    return item.currentPrice;
  } else if (item.lastPrice !== undefined) {
    return item.lastPrice;
  } else {
    return createEmptyPrice();
  }
};

// Just a helper, DRY
export const saveItemParameter = async (itemId: CollectionItemId, data: ApiData_UpdateCollectionItem) => {
  return updateCollectionItem(itemId, data)
    .then(() => EventBus.emit({ event: Event.ToastSuccess, data: gettext.$gettext("Uloženo") }))
    .catch((e: unknown) => {
      // @todo Better error recognition would be nice.
      EventBus.emit({ event: Event.ToastError, data: gettext.$gettext("Nepodařilo se uložit.") });
      throw e;
    });
};

export const hasCollectibleDetail = (item: CollectionItem): item is CollectionItemWithDetail => "detail" in item && item.detail !== undefined;
export const hasCollectibleLegoSetsDetail = (item: CollectionItem): item is CollectionItemWithDetail_LegoSet => {
  return "detail" in item && item.detail !== undefined && item.categoryId === CategoryId.LegoSets;
};
export const hasCollectible = (item: CollectionItem): item is CollectionItemWithCollectible => "collectible" in item && item.collectible !== undefined && item.collectible !== null;

export const myCollectionItemInjectionKey = Symbol() as InjectionKey<Ref<CollectionItem | undefined>>;

export const hasCollectibleElastic = (item: CollectionItem): item is CollectionItem & { collectibleElastic: CollectibleElastic } => {
  return item.collectibleElastic !== null;
};
